import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { collection, getDoc, getDocs, doc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../config/fbConfig';
import Loader from "./Loader";
import { syncSubjectsWithStudents } from './SyncSubjects';

Modal.setAppElement('#root');

const AddQuestionsToSubjectModal = ({ isOpen, onClose, classId, subject, onSuccess }) => {
  const [questionSets, setQuestionSets] = useState([]);
  const [selectedQuestionSetId, setSelectedQuestionSetId] = useState(null);
  const [questionCount, setQuestionCount] = useState('');
  const [marksPerQuestion, setMarksPerQuestion] = useState('');
  const [activationDate, setActivationDate] = useState('');
  const [deactivationDate, setDeactivationDate] = useState('');
  const [duration, setDuration] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingRemove, setLoadingRemove] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!isOpen || !subject?.id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch question sets for the specific subject
        const questionQuery = query(
          collection(db, 'questions'),
          where('subjectId', '==', subject.id)
        );
        const questionSnap = await getDocs(questionQuery);
        const classesSnap = await getDocs(collection(db, 'classes'));
        const termsSnap = await getDocs(collection(db, 'terms'));
        const subjectsSnap = await getDocs(collection(db, 'subjects'));

        // Map classes, terms, and subjects for name lookup
        const classesMap = {};
        classesSnap.docs.forEach(doc => {
          classesMap[doc.id] = doc.data().name;
        });

        const termsMap = {};
        termsSnap.docs.forEach(doc => {
          termsMap[doc.id] = doc.data().name;
        });

        const subjectsMap = {};
        subjectsSnap.docs.forEach(doc => {
          subjectsMap[doc.id] = doc.data().name;
        });

        // Fetch the class's current subject assignments
        const classRef = doc(db, 'classes', classId);
        const classSnap = await getDoc(classRef);
        const classData = classSnap.exists() ? classSnap.data() : {};
        const currentSubjects = classData.subjects || [];
        const currentSubject = currentSubjects.find(
          sub => sub.id === subject.id
        );
        const assignedQuestionSetId = currentSubject?.questionSetId || null;
        const assignedQuestionCount = currentSubject?.questionCount || '';
        const assignedActivationDate = currentSubject?.activationDate || '';
        const assignedDeactivationDate = currentSubject?.deactivationDate || '';
        const assignedDuration = currentSubject?.duration || '';

        // Process question sets
        const allQuestionSets = questionSnap.docs.map(doc => ({
          id: doc.id,
          classId: doc.data().classId,
          termId: doc.data().termId,
          subjectId: doc.data().subjectId,
          questionCount: doc.data().questions?.length || 0,
          className: classesMap[doc.data().classId] || 'Unknown',
          termName: termsMap[doc.data().termId] || 'Unknown',
          subjectName: subjectsMap[doc.data().subjectId] || 'Unknown'
        }));

        setQuestionSets(allQuestionSets);
        setSelectedQuestionSetId(assignedQuestionSetId);
        setQuestionCount(assignedQuestionCount);
        setMarksPerQuestion(assignedQuestionCount ? (60 / assignedQuestionCount).toFixed(2) : '');
        setActivationDate(assignedActivationDate);
        setDeactivationDate(assignedDeactivationDate);
        setDuration(assignedDuration);
      } catch (error) {
        console.error("Error fetching question sets:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, classId, subject]);

  const handleRadioChange = (questionSetId) => {
    setSelectedQuestionSetId(questionSetId);
  };

  const handleQuestionCountChange = (e) => {
    const count = e.target.value;
    setQuestionCount(count);
    if (count && !isNaN(count) && count > 0) {
      setMarksPerQuestion((60 / count).toFixed(2));
    } else {
      setMarksPerQuestion('');
    }
  };

  const handleAssign = async () => {
    setLoading(true);
    try {
      const classRef = doc(db, 'classes', classId);
      const classSnap = await getDoc(classRef);
      if (!classSnap.exists()) return;

      const classData = classSnap.data();
      const currentSubjects = classData.subjects || [];

      // Update the subject with the selected question set and new fields
      const updatedSubjects = currentSubjects.map(sub => {
        if (sub.id === subject.id) {
          return {
            ...sub,
            questionSetId: selectedQuestionSetId || '',
            questionCount: parseInt(questionCount) || 0,
            marksPerQuestion: parseFloat(marksPerQuestion) || 0,
            activationDate: activationDate || '',
            deactivationDate: deactivationDate || '',
            duration: parseInt(duration) || 0
          };
        }
        return sub;
      });

      // Ensure the subject exists in the class
      if (!currentSubjects.some(sub => sub.id === subject.id)) {
        updatedSubjects.push({
          id: subject.id,
          teacherId: '',
          questionSetId: selectedQuestionSetId || '',
          questionCount: parseInt(questionCount) || 0,
          marksPerQuestion: parseFloat(marksPerQuestion) || 0,
          activationDate: activationDate || '',
          deactivationDate: deactivationDate || '',
          duration: parseInt(duration) || 0
        });
      }

      await updateDoc(classRef, { subjects: updatedSubjects });
      await syncSubjectsWithStudents(classId);

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error assigning question set:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    setLoadingRemove(true);
    try {
      const classRef = doc(db, 'classes', classId);
      const classSnap = await getDoc(classRef);
      if (!classSnap.exists()) return;

      const classData = classSnap.data();
      const currentSubjects = classData.subjects || [];

      // Remove the question set and related fields from the subject
      const updatedSubjects = currentSubjects.map(sub => {
        if (sub.id === subject.id) {
          return {
            ...sub,
            questionSetId: '',
            questionCount: 0,
            marksPerQuestion: 0,
            activationDate: '',
            deactivationDate: '',
            duration: 0
          };
        }
        return sub;
      });

      await updateDoc(classRef, { subjects: updatedSubjects });
      await syncSubjectsWithStudents(classId);

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error removing question set:", error);
    } finally {
      setLoadingRemove(false);
    }
  };

  const filteredQuestionSets = questionSets.filter(
    questionSet =>
      questionSet.subjectName.toLowerCase().includes(filter.toLowerCase()) ||
      questionSet.className.toLowerCase().includes(filter.toLowerCase()) ||
      questionSet.termName.toLowerCase().includes(filter.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="custom-modal"
      overlayClassName="custom-modal-overlay"
    >
      <p className="size5 caps">Assign Question Set to {subject?.name}</p>

      {loading ? (
        <Loader text={"LOADING"} />
      ) : (
        <>
          <div className='row space-between gap-15'>
            <div>
              <label>
                No of Questions
                <input
                  type="number"
                  value={questionCount}
                  onChange={handleQuestionCountChange}
                  className="input-field"
                  min="1"
                  placeholder="Enter number of questions"
                />
              </label>
            </div>
            <div>
              <label>
                Duration (mins)
                <input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="input-field"
                  min="1"
                  placeholder="Duration in minutes"
                />
              </label>
            </div>
          </div>

          <div>
            <label>
              Activation Date and Time
              <input
                type="datetime-local"
                value={activationDate}
                onChange={(e) => setActivationDate(e.target.value)}
                className="input-field"
              />
            </label>
          </div>

          <div>
            <label>
              Deactivation Date and Time
              <input
                type="datetime-local"
                value={deactivationDate}
                onChange={(e) => setDeactivationDate(e.target.value)}
                className="input-field"
              />
            </label>
          </div>          
          <p className='size5 bold caps'>Questions</p>
          <div className='modal-items-scroll'>
            {filteredQuestionSets.length === 0 ? (
              <p className="male-female-stat">No question sets found.</p>
            ) : (
              filteredQuestionSets.map(questionSet => (
                <label key={questionSet.id} style={{ display: 'block', marginBottom: '8px' }}>
                  <input
                    type="radio"
                    className="input-checkbox gold-tick male-female-stat"
                    checked={selectedQuestionSetId === questionSet.id}
                    onChange={() => handleRadioChange(questionSet.id)}
                  />
                  {questionSet.className} / {questionSet.termName} / {questionSet.subjectName} ({questionSet.questionCount} questions)
                  {selectedQuestionSetId === questionSet.id && (
                    <span style={{ color: 'green', fontSize: '0.7rem' }}> (Assigned)</span>
                  )}
                </label>
              ))
            )}
          </div>

          <div className="row align-center gap-1" style={{ marginTop: '16px' }}>
            <button
              onClick={onClose}
              className="login-button"
              disabled={loading || loadingRemove}
            >
              Cancel
            </button>
            <button
              onClick={handleRemove}
              className="login-button"
              disabled={loading || loadingRemove || !selectedQuestionSetId}
            >
              {loadingRemove ? 'Removing...' : 'Remove'}
            </button>
            <button
              onClick={handleAssign}
              className="login-button"
              disabled={loading || loadingRemove}
            >
              {loading ? 'Assigning...' : 'Assign'}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default AddQuestionsToSubjectModal;